import React, { useState, useEffect, useRef } from 'react';
import {
  BriefcaseIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  ArrowRightIcon,
  ArrowLeftIcon,
  DocumentTextIcon,
  DocumentMagnifyingGlassIcon,
  PencilSquareIcon,
  ClipboardDocumentListIcon,
  ArrowUpTrayIcon,
  ArrowDownTrayIcon,
  XMarkIcon,
  MapPinIcon,
  PrinterIcon,
} from '@heroicons/react/24/outline';
import apiClient, { exportAPI } from '../services/api';
import { useTranslation } from 'react-i18next';
import { marked } from 'marked';
import { ExportDropdownMenu } from '../components/ui/ExportDropdownMenu';
import { LoadingOverlay } from '../components/ui/LoadingOverlay';
import ReactMarkdown from 'react-markdown';

// Types
interface Country {
  code: string;
  name: string;
  regions_label: string;
}

interface Region {
  code: string;
  name: string;
}

interface SituationType {
  id: string;
  name: string;
  description: string;
  indicators: string[];
}

// Workflow types
type WorkflowType = null | 'analyze' | 'generate';

// Step definitions for each workflow
const ANALYZE_STEPS = ['jurisdiction', 'uploadDocument', 'analysis'];
const GENERATE_STEPS = ['jurisdiction', 'issueAndDetails', 'generatedLetter'];

export const EmploymentAdvisor: React.FC = () => {
  const { t, i18n } = useTranslation();

  // Workflow selection
  const [selectedWorkflow, setSelectedWorkflow] = useState<WorkflowType>(null);
  const [currentStep, setCurrentStep] = useState(0);

  // Jurisdiction selection (shared by both workflows)
  const [countries, setCountries] = useState<Country[]>([]);
  const [selectedCountry, setSelectedCountry] = useState('');
  const [regions, setRegions] = useState<Region[]>([]);
  const [selectedRegion, setSelectedRegion] = useState('');
  const [regionsLabel, setRegionsLabel] = useState('State');

  // Document Analysis workflow state
  const [analyzeFile, setAnalyzeFile] = useState<File | null>(null);
  const [analyzeDocumentText, setAnalyzeDocumentText] = useState('');
  const [documentAnalysis, setDocumentAnalysis] = useState<string | null>(null);
  const [extractedFields, setExtractedFields] = useState<Record<string, any> | null>(null);
  const analyzeFileInputRef = useRef<HTMLInputElement>(null);

  // Letter Generator workflow state
  const [situationTypes, setSituationTypes] = useState<SituationType[]>([]);
  const [selectedSituationType, setSelectedSituationType] = useState('');
  const [letterEmployeeName, setLetterEmployeeName] = useState('');
  const [letterEmployeeAddress, setLetterEmployeeAddress] = useState('');
  const [letterEmployeeCity, setLetterEmployeeCity] = useState('');
  const [letterEmployeeZip, setLetterEmployeeZip] = useState('');
  const [letterEmployerName, setLetterEmployerName] = useState('');
  const [letterEmployerAddress, setLetterEmployerAddress] = useState('');
  const [letterPosition, setLetterPosition] = useState('');
  const [letterStartDate, setLetterStartDate] = useState('');
  const [letterEndDate, setLetterEndDate] = useState('');
  const [letterDetails, setLetterDetails] = useState('');
  const [generatedLetter, setGeneratedLetter] = useState<string | null>(null);
  const [letterTips, setLetterTips] = useState<string[]>([]);

  // Loading states
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load initial data
  useEffect(() => {
    loadCountries();
    loadSituationTypes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load regions when country changes
  useEffect(() => {
    if (selectedCountry) {
      loadRegions();
      setSelectedRegion('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCountry]);

  const loadCountries = async () => {
    try {
      const response = await apiClient.get('/employment/countries');
      setCountries(response.data.countries);
    } catch (err) {
      setError(t('employment:errors.loadCountries', 'Failed to load countries'));
    }
  };

  const loadRegions = async () => {
    try {
      const response = await apiClient.get(`/employment/regions/${selectedCountry}`);
      setRegions(response.data.regions);
      setRegionsLabel(response.data.regions_label);
    } catch (err) {
      setError(t('employment:errors.loadRegions', 'Failed to load regions'));
    }
  };

  const loadSituationTypes = async () => {
    try {
      const response = await apiClient.get('/employment/situation-types');
      setSituationTypes(response.data.situation_types);
    } catch (err) {
      console.error('Failed to load situation types:', err);
    }
  };

  // Document Analysis handlers
  const handleAnalyzeFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const validTypes = ['application/pdf', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'application/msword', 'text/plain'];
      if (validTypes.includes(file.type) || file.name.endsWith('.pdf') || file.name.endsWith('.docx') || file.name.endsWith('.doc') || file.name.endsWith('.txt')) {
        setAnalyzeFile(file);
        setExtractedFields(null);
        setAnalyzeDocumentText('');
      } else {
        setError(t('employment:errors.invalidFileType', 'Please upload a PDF, Word, or TXT document'));
      }
    }
  };

  const clearAnalyzeFile = () => {
    setAnalyzeFile(null);
    setExtractedFields(null);
    if (analyzeFileInputRef.current) {
      analyzeFileInputRef.current.value = '';
    }
  };

  const analyzeDocument = async () => {
    if (!analyzeFile && !analyzeDocumentText.trim()) return;

    setIsLoading(true);
    setError(null);

    try {
      const formData = new FormData();

      if (analyzeFile) {
        formData.append('file', analyzeFile);
      } else {
        const blob = new Blob([analyzeDocumentText], { type: 'text/plain' });
        const file = new File([blob], 'pasted-document.txt', { type: 'text/plain' });
        formData.append('file', file);
      }

      formData.append('country', selectedCountry);
      formData.append('region', selectedRegion);
      formData.append('language', i18n.language);

      const response = await apiClient.post('/employment/analyze-document-file', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      setDocumentAnalysis(response.data.analysis);

      // Also extract fields for display
      if (response.data.extracted_fields) {
        setExtractedFields(response.data.extracted_fields);
      }
    } catch (err) {
      setError(t('employment:errors.analyzeDocument', 'Failed to analyze document'));
    } finally {
      setIsLoading(false);
    }
  };

  // Letter Generator handlers
  const generateLetter = async () => {
    if (!selectedSituationType || !letterDetails.trim()) return;

    setIsLoading(true);
    setError(null);

    // Get today's date in user's local format
    const today = new Date().toLocaleDateString(i18n.language, {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });

    // Build sender address string
    const senderAddress = [
      letterEmployeeAddress,
      letterEmployeeCity && letterEmployeeZip
        ? `${letterEmployeeCity}, ${letterEmployeeZip}`
        : letterEmployeeCity || letterEmployeeZip
    ].filter(Boolean).join('\n');

    try {
      const response = await apiClient.post('/employment/generate-document', {
        country: selectedCountry,
        region: selectedRegion,
        document_type: selectedSituationType,
        employee_name: letterEmployeeName || undefined,
        sender_address: senderAddress || undefined,
        employer_name: letterEmployerName || undefined,
        employer_address: letterEmployerAddress || undefined,
        position: letterPosition || undefined,
        start_date: letterStartDate || undefined,
        end_date: letterEndDate || undefined,
        letter_date: today,
        additional_details: letterDetails,
        language: i18n.language
      });
      setGeneratedLetter(response.data.document);
      setLetterTips(response.data.tips || []);
    } catch (err) {
      setError(t('employment:errors.generateDocument', 'Failed to generate letter'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleExportAnalysis = async (format: 'word' | 'pdf' | 'text') => {
    await exportAnalysis(format);
  };

  const handleExportLetter = async (format: 'word' | 'pdf' | 'text') => {
    await exportLetter(format);
  };

  const printLetter = () => {
    if (!generatedLetter) return;

    const situationType = situationTypes.find(s => s.id === selectedSituationType);
    // Convert markdown to HTML using marked
    const letterHtml = marked(generatedLetter, { async: false }) as string;
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>${situationType?.name || 'Employment Letter'}</title>
          <style>
            body {
              font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif;
              line-height: 1.6;
              color: #1f2937;
              max-width: 800px;
              margin: 0 auto;
              padding: 40px 20px;
            }
            h1 { font-size: 24px; color: #1e40af; }
            h2 { font-size: 20px; color: #1f2937; margin-top: 24px; }
            h3 { font-size: 16px; color: #374151; margin-top: 16px; }
            p { margin: 12px 0; }
            ul, ol { margin: 8px 0; padding-left: 24px; }
            li { margin-bottom: 6px; }
            strong { font-weight: 600; }
            em { font-style: italic; }
            code { background: #f3f4f6; padding: 2px 6px; border-radius: 4px; font-family: monospace; }
            pre { background: #f3f4f6; padding: 12px; border-radius: 8px; overflow-x: auto; }
            pre code { background: none; padding: 0; }
            blockquote { border-left: 4px solid #2563eb; margin: 16px 0; padding-left: 16px; color: #666; }
            @media print {
              body { padding: 20px; }
            }
          </style>
        </head>
        <body>
          <div class="content">${letterHtml}</div>
        </body>
      </html>
    `);

    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
    printWindow.close();
  };

  const exportLetter = async (format: 'word' | 'text' | 'pdf') => {
    if (!generatedLetter) return;

    try {
      const situationType = situationTypes.find(s => s.id === selectedSituationType);
      const extensionMap: Record<string, string> = { word: 'docx', pdf: 'pdf', text: 'txt' };
      const extension = extensionMap[format];
      const filename = `employment-letter-${selectedSituationType.replace(/_/g, '-')}.${extension}`;

      const blob = await exportAPI.exportDocument({
        content: generatedLetter,
        title: situationType?.name || 'Employment Letter',
        format: extension as 'docx' | 'pdf' | 'txt'
      });

      if ('showSaveFilePicker' in window) {
        try {
          const mimeTypes: Record<string, string> = {
            docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
            pdf: 'application/pdf',
            txt: 'text/plain'
          };

          const descriptions: Record<string, string> = {
            word: 'Word Document',
            pdf: 'PDF Document',
            text: 'Text File'
          };

          const handle = await (window as any).showSaveFilePicker({
            suggestedName: filename,
            types: [{
              description: descriptions[format],
              accept: { [mimeTypes[extension]]: [`.${extension}`] }
            }]
          });

          const writable = await handle.createWritable();
          await writable.write(blob);
          await writable.close();
          return;
        } catch (err: any) {
          if (err.name !== 'AbortError') {
            console.warn('File System Access API failed, falling back to standard download:', err);
          } else {
            return;
          }
        }
      }

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Export error:', error);
      setError(t('employment:errors.exportFailed', 'Failed to export document. Please try again.'));
    }
  };

  // Analysis print/export functions
  const printAnalysis = () => {
    if (!documentAnalysis) return;

    const locationText = `${regions.find(r => r.code === selectedRegion)?.name}, ${countries.find(c => c.code === selectedCountry)?.name}`;
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    // Convert markdown to HTML using marked
    const htmlContent = marked(documentAnalysis, { async: false }) as string;

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Employment Document Analysis</title>
          <style>
            body {
              font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif;
              line-height: 1.6;
              color: #1f2937;
              max-width: 800px;
              margin: 0 auto;
              padding: 40px 20px;
            }
            h1 { font-size: 24px; color: #1e40af; margin-bottom: 8px; }
            h2 { font-size: 18px; color: #1f2937; border-bottom: 1px solid #e5e7eb; padding-bottom: 8px; margin-top: 24px; }
            h3 { font-size: 16px; color: #374151; margin-top: 16px; }
            p { margin: 12px 0; }
            li { margin: 4px 0; margin-left: 20px; }
            strong { color: #111827; }
            .header { margin-bottom: 24px; padding-bottom: 16px; border-bottom: 2px solid #1e40af; }
            .jurisdiction { color: #6b7280; font-size: 14px; }
            .disclaimer { margin-top: 32px; padding: 16px; background: #fef3c7; border: 1px solid #f59e0b; border-radius: 8px; font-size: 12px; }
            @media print {
              body { padding: 20px; }
              .disclaimer { break-inside: avoid; }
            }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>Employment Document Analysis</h1>
            <div class="jurisdiction">Jurisdiction: ${locationText}</div>
            <div class="jurisdiction">Generated: ${new Date().toLocaleDateString()}</div>
          </div>
          <div class="content">${htmlContent}</div>
          <div class="disclaimer">
            <strong>Legal Disclaimer:</strong> This analysis is for informational purposes only and does not constitute legal advice.
            Have an attorney review any employment document before signing. Employment laws vary by jurisdiction and change over time.
          </div>
        </body>
      </html>
    `);

    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
    printWindow.close();
  };

  const exportAnalysis = async (format: 'word' | 'text' | 'pdf') => {
    if (!documentAnalysis) return;

    try {
      const locationText = `${regions.find(r => r.code === selectedRegion)?.name}, ${countries.find(c => c.code === selectedCountry)?.name}`;
      const extensionMap: Record<string, string> = { word: 'docx', pdf: 'pdf', text: 'txt' };
      const extension = extensionMap[format];
      const filename = `employment-document-analysis-${new Date().toISOString().split('T')[0]}.${extension}`;

      const headerText = `Employment Document Analysis\nJurisdiction: ${locationText}\nGenerated: ${new Date().toLocaleDateString()}\n\n`;
      const footerText = `\n\n---\nLegal Disclaimer: This analysis is for informational purposes only and does not constitute legal advice. Have an attorney review any employment document before signing.`;
      const fullContent = headerText + documentAnalysis + footerText;

      const blob = await exportAPI.exportDocument({
        content: fullContent,
        title: 'Employment Document Analysis',
        format: extension as 'docx' | 'pdf' | 'txt'
      });

      if ('showSaveFilePicker' in window) {
        try {
          const mimeTypes: Record<string, string> = {
            docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
            pdf: 'application/pdf',
            txt: 'text/plain'
          };

          const descriptions: Record<string, string> = {
            word: 'Word Document',
            pdf: 'PDF Document',
            text: 'Text File'
          };

          const handle = await (window as any).showSaveFilePicker({
            suggestedName: filename,
            types: [{
              description: descriptions[format],
              accept: { [mimeTypes[extension]]: [`.${extension}`] }
            }]
          });

          const writable = await handle.createWritable();
          await writable.write(blob);
          await writable.close();
          return;
        } catch (err: any) {
          if (err.name !== 'AbortError') {
            console.warn('File System Access API failed, falling back to standard download:', err);
          } else {
            return;
          }
        }
      }

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Export error:', error);
      setError(t('employment:errors.exportFailed', 'Failed to export document. Please try again.'));
    }
  };

  const getCurrentSteps = () => {
    if (selectedWorkflow === 'analyze') return ANALYZE_STEPS;
    if (selectedWorkflow === 'generate') return GENERATE_STEPS;
    return [];
  };

  const canProceed = () => {
    const steps = getCurrentSteps();
    const currentStepName = steps[currentStep];

    switch (currentStepName) {
      case 'jurisdiction':
        return selectedCountry && selectedRegion;
      case 'uploadDocument':
        return analyzeFile || analyzeDocumentText.trim().length > 0;
      case 'issueAndDetails':
        return !!selectedSituationType && letterDetails.trim().length > 0;
      default:
        return true;
    }
  };

  const handleNext = async () => {
    const steps = getCurrentSteps();
    const currentStepName = steps[currentStep];

    // Auto-trigger analysis when moving from upload to analysis step
    if (selectedWorkflow === 'analyze' && currentStepName === 'uploadDocument') {
      await analyzeDocument();
    }

    // Auto-trigger letter generation when moving from issueAndDetails to generated letter
    if (selectedWorkflow === 'generate' && currentStepName === 'issueAndDetails') {
      await generateLetter();
    }

    setCurrentStep(Math.min(steps.length - 1, currentStep + 1));
  };

  const handleBack = () => {
    if (currentStep === 0) {
      // Go back to workflow selection
      setSelectedWorkflow(null);
      resetState();
    } else {
      setCurrentStep(Math.max(0, currentStep - 1));
    }
  };

  const resetState = () => {
    setCurrentStep(0);
    setSelectedCountry('');
    setSelectedRegion('');
    setAnalyzeFile(null);
    setAnalyzeDocumentText('');
    setDocumentAnalysis(null);
    setExtractedFields(null);
    setSelectedSituationType('');
    setLetterEmployeeName('');
    setLetterEmployerName('');
    setLetterEmployerAddress('');
    setLetterPosition('');
    setLetterStartDate('');
    setLetterEndDate('');
    setLetterDetails('');
    setGeneratedLetter(null);
    setLetterTips([]);
    setError(null);
  };

  const selectWorkflow = (workflow: WorkflowType) => {
    resetState();
    setSelectedWorkflow(workflow);
  };

  // Render workflow selection
  const renderWorkflowSelection = () => (
    <div className="space-y-8">
      <div className="text-center">
        <p className="text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
          {t('employment:workflowSelection.description', 'Choose what you need help with today. You can analyze employment documents you\'ve received, or generate professional letters to address workplace issues.')}
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Analyze Document Card */}
        <button
          onClick={() => selectWorkflow('analyze')}
          className="group p-6 bg-white dark:bg-gray-800 rounded-xl border-2 border-blue-200 dark:border-blue-800 hover:border-blue-500 dark:hover:border-blue-500 hover:shadow-lg transition-all text-left"
        >
          <div className="flex items-center mb-4">
            <div className="p-3 bg-blue-100 dark:bg-blue-900/50 rounded-lg group-hover:bg-blue-200 dark:group-hover:bg-blue-900 transition-colors">
              <DocumentMagnifyingGlassIcon className="h-8 w-8 text-blue-600 dark:text-blue-400" />
            </div>
          </div>
          <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
            {t('employment:workflowSelection.analyzeTitle', 'Analyze a Document')}
          </h3>
          <p className="text-gray-600 dark:text-gray-400 mb-4">
            {t('employment:workflowSelection.analyzeDescription', 'Upload an employment document you received (offer letter, contract, NDA, PIP, termination notice) to get AI-powered analysis and understand your rights.')}
          </p>
          <div className="flex flex-wrap gap-2">
            {['Offer Letters', 'Contracts', 'NDAs', 'PIPs', 'Termination Notices'].map((type) => (
              <span key={type} className="px-2 py-1 text-xs bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded">
                {type}
              </span>
            ))}
          </div>
          <div className="mt-4 flex items-center text-blue-600 dark:text-blue-400 font-medium">
            {t('employment:workflowSelection.getStarted', 'Get Started')}
            <ArrowRightIcon className="h-5 w-5 ml-2 group-hover:translate-x-1 transition-transform" />
          </div>
        </button>

        {/* Generate Letter Card */}
        <button
          onClick={() => selectWorkflow('generate')}
          className="group p-6 bg-white dark:bg-gray-800 rounded-xl border-2 border-green-200 dark:border-green-800 hover:border-green-500 dark:hover:border-green-500 hover:shadow-lg transition-all text-left"
        >
          <div className="flex items-center mb-4">
            <div className="p-3 bg-green-100 dark:bg-green-900/50 rounded-lg group-hover:bg-green-200 dark:group-hover:bg-green-900 transition-colors">
              <PencilSquareIcon className="h-8 w-8 text-green-600 dark:text-green-400" />
            </div>
          </div>
          <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
            {t('employment:workflowSelection.generateTitle', 'Generate a Letter')}
          </h3>
          <p className="text-gray-600 dark:text-gray-400 mb-4">
            {t('employment:workflowSelection.generateDescription', 'Create a professional letter to address a workplace issue. Get AI-generated content tailored to your specific situation and jurisdiction.')}
          </p>
          <div className="flex flex-wrap gap-2">
            {['Wrongful Termination', 'Wage Theft', 'Discrimination', 'Retaliation', 'Harassment'].map((type) => (
              <span key={type} className="px-2 py-1 text-xs bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-300 rounded">
                {type}
              </span>
            ))}
          </div>
          <div className="mt-4 flex items-center text-green-600 dark:text-green-400 font-medium">
            {t('employment:workflowSelection.getStarted', 'Get Started')}
            <ArrowRightIcon className="h-5 w-5 ml-2 group-hover:translate-x-1 transition-transform" />
          </div>
        </button>
      </div>
    </div>
  );

  // Render jurisdiction step (shared)
  const renderJurisdiction = () => (
    <div className="space-y-6">
      <p className="text-gray-600 dark:text-gray-400">
        {t('employment:jurisdiction.description', 'Select your location so we can provide jurisdiction-specific guidance and ensure compliance with local employment laws.')}
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            {t('employment:jurisdiction.selectCountry', 'Select Country')}
          </label>
          <select
            value={selectedCountry}
            onChange={(e) => setSelectedCountry(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
          >
            <option value="">{t('employment:jurisdiction.selectCountryPrompt', 'Select a country...')}</option>
            {countries.map((country) => (
              <option key={country.code} value={country.code}>
                {country.name}
              </option>
            ))}
          </select>
        </div>

        {selectedCountry && (
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t('employment:jurisdiction.selectRegion', 'Select {{regionLabel}}', { regionLabel: regionsLabel })}
            </label>
            <select
              value={selectedRegion}
              onChange={(e) => setSelectedRegion(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            >
              <option value="">{t('employment:jurisdiction.selectRegionPrompt', 'Select a {{regionLabel}}...', { regionLabel: regionsLabel.toLowerCase() })}</option>
              {regions.map((region) => (
                <option key={region.code} value={region.code}>
                  {region.name}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      {selectedCountry && selectedRegion && (
        <div className="p-4 bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-800 rounded-lg">
          <div className="flex items-center">
            <CheckCircleIcon className="h-5 w-5 text-green-600 dark:text-green-400 mr-2" />
            <span className="text-green-800 dark:text-green-200">
              {t('employment:jurisdiction.selectedLocation', 'Selected Location')}: {regions.find(r => r.code === selectedRegion)?.name}, {countries.find(c => c.code === selectedCountry)?.name}
            </span>
          </div>
        </div>
      )}
    </div>
  );

  // Render upload document step (Analyze workflow)
  const renderUploadDocument = () => (
    <div className="space-y-6">
      <p className="text-gray-600 dark:text-gray-400">
        {t('employment:analyze.uploadDescription', 'Upload or paste the employment document you want analyzed. We\'ll review it for key terms, potential issues, and provide guidance based on your jurisdiction.')}
      </p>

      {/* Option 1: Upload Document */}
      <div className="p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
        <h4 className="font-medium text-blue-900 dark:text-blue-100 mb-3 flex items-center">
          <ArrowUpTrayIcon className="h-5 w-5 mr-2" />
          {t('employment:analyze.uploadTitle', 'Option 1: Upload Document')}
        </h4>
        <p className="text-sm text-blue-700 dark:text-blue-300 mb-3">
          {t('employment:analyze.uploadHint', 'Upload a PDF, Word, or text document.')}
        </p>
        <div className="flex flex-wrap items-center gap-3">
          <input
            ref={analyzeFileInputRef}
            type="file"
            accept=".pdf,.doc,.docx,.txt"
            onChange={handleAnalyzeFileSelect}
            className="hidden"
            id="analyze-file-input"
          />
          <button
            type="button"
            onClick={() => analyzeFileInputRef.current?.click()}
            disabled={isLoading}
            className="flex items-center gap-2 px-4 py-2 bg-white dark:bg-gray-700 border border-blue-300 dark:border-blue-600 rounded-lg hover:bg-blue-50 dark:hover:bg-gray-600 transition-colors"
          >
            <ArrowUpTrayIcon className="h-5 w-5 text-blue-600 dark:text-blue-400" />
            <span className="text-blue-700 dark:text-blue-300">
              {t('employment:document.selectDocument', 'Select Document')}
            </span>
          </button>
          {analyzeFile && (
            <div className="flex items-center gap-2 px-3 py-2 bg-white dark:bg-gray-700 rounded-lg border border-blue-200 dark:border-blue-700">
              <DocumentTextIcon className="h-5 w-5 text-blue-600 dark:text-blue-400" />
              <span className="text-sm text-blue-800 dark:text-blue-200 max-w-[200px] truncate">
                {analyzeFile.name}
              </span>
              <button
                onClick={clearAnalyzeFile}
                className="p-1 hover:bg-blue-100 dark:hover:bg-blue-800 rounded"
              >
                <XMarkIcon className="h-4 w-4 text-blue-600 dark:text-blue-400" />
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Divider */}
      <div className="relative">
        <div className="absolute inset-0 flex items-center">
          <div className="w-full border-t border-gray-300 dark:border-gray-600"></div>
        </div>
        <div className="relative flex justify-center text-sm">
          <span className="px-4 bg-white dark:bg-gray-800 text-gray-500 dark:text-gray-400">
            {t('employment:analyze.or', 'OR')}
          </span>
        </div>
      </div>

      {/* Option 2: Paste/Type Text */}
      <div className="p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
        <h4 className="font-medium text-green-900 dark:text-green-100 mb-3 flex items-center">
          <ClipboardDocumentListIcon className="h-5 w-5 mr-2" />
          {t('employment:analyze.pasteTitle', 'Option 2: Paste or Type Document Text')}
        </h4>
        <p className="text-sm text-green-700 dark:text-green-300 mb-3">
          {t('employment:analyze.pasteHint', 'Copy and paste the contents of your document, or type it directly.')}
        </p>
        <textarea
          value={analyzeDocumentText}
          onChange={(e) => {
            setAnalyzeDocumentText(e.target.value);
            setExtractedFields(null);
            if (analyzeFile) {
              clearAnalyzeFile();
            }
          }}
          rows={10}
          placeholder={t('employment:analyze.pastePlaceholder', 'Paste or type your employment document here...')}
          className="w-full px-4 py-3 border border-green-300 dark:border-green-600 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white font-mono text-sm"
        />
      </div>

      {/* Status message */}
      {(analyzeFile || analyzeDocumentText.trim()) && (
        <div className="p-4 bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-800 rounded-lg">
          <div className="flex items-center">
            <CheckCircleIcon className="h-5 w-5 text-green-600 dark:text-green-400 mr-2" />
            <span className="text-green-800 dark:text-green-200">
              {analyzeFile
                ? t('employment:analyze.fileReady', 'Document ready for analysis. Click Next to analyze.')
                : t('employment:analyze.textReady', 'Document text ready for analysis. Click Next to analyze.')}
            </span>
          </div>
        </div>
      )}
    </div>
  );

  // Render analysis results step
  const renderAnalysisResults = () => (
    <div className="space-y-6">
      {isLoading ? (
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-blue-500 border-t-transparent"></div>
          <p className="mt-4 text-gray-600 dark:text-gray-400">
            {t('employment:analyze.analyzing', 'Analyzing your document...')}
          </p>
        </div>
      ) : documentAnalysis ? (
        <>
          <div className="p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
            <div className="flex justify-between items-start">
              <div>
                <h4 className="font-medium text-blue-900 dark:text-blue-100 mb-2 flex items-center">
                  <DocumentMagnifyingGlassIcon className="h-5 w-5 mr-2" />
                  {t('employment:analyze.resultsTitle', 'Document Analysis')}
                </h4>
                <p className="text-sm text-blue-700 dark:text-blue-300">
                  {t('employment:analyze.resultsJurisdiction', 'Based on laws in {{location}}', {
                    location: `${regions.find(r => r.code === selectedRegion)?.name}, ${countries.find(c => c.code === selectedCountry)?.name}`
                  })}
                </p>
              </div>
              <ExportDropdownMenu
                onExport={handleExportAnalysis}
                onPrint={printAnalysis}
                showPrint={true}
              />
            </div>
          </div>

          {extractedFields && Object.keys(extractedFields).some(k => extractedFields[k]) && (
            <div className="p-4 bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 rounded-lg">
              <h4 className="font-medium text-gray-900 dark:text-white mb-3">
                {t('employment:analyze.extractedInfo', 'Extracted Information')}
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                {extractedFields.document_type && extractedFields.document_type !== 'unknown' && (
                  <div>
                    <span className="text-gray-500 dark:text-gray-400">{t('employment:analyze.docType', 'Document Type')}:</span>
                    <span className="ml-2 text-gray-900 dark:text-white font-medium">{extractedFields.document_type.replace(/_/g, ' ')}</span>
                  </div>
                )}
                {extractedFields.employee_name && (
                  <div>
                    <span className="text-gray-500 dark:text-gray-400">{t('employment:analyze.employee', 'Employee')}:</span>
                    <span className="ml-2 text-gray-900 dark:text-white font-medium">{extractedFields.employee_name}</span>
                  </div>
                )}
                {extractedFields.employer_name && (
                  <div>
                    <span className="text-gray-500 dark:text-gray-400">{t('employment:analyze.employer', 'Employer')}:</span>
                    <span className="ml-2 text-gray-900 dark:text-white font-medium">{extractedFields.employer_name}</span>
                  </div>
                )}
                {extractedFields.position && (
                  <div>
                    <span className="text-gray-500 dark:text-gray-400">{t('employment:analyze.position', 'Position')}:</span>
                    <span className="ml-2 text-gray-900 dark:text-white font-medium">{extractedFields.position}</span>
                  </div>
                )}
                {extractedFields.salary && (
                  <div>
                    <span className="text-gray-500 dark:text-gray-400">{t('employment:analyze.salary', 'Salary')}:</span>
                    <span className="ml-2 text-gray-900 dark:text-white font-medium">{extractedFields.salary}</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Rendered markdown analysis */}
          <div className="p-6 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg">
            <div className="analysis-content space-y-6">
              <style>{`
                .analysis-content h2 {
                  font-size: 1.25rem;
                  font-weight: 700;
                  color: #1e40af;
                  border-bottom: 2px solid #e5e7eb;
                  padding-bottom: 0.5rem;
                  margin-top: 2rem;
                  margin-bottom: 1rem;
                }
                .dark .analysis-content h2 {
                  color: #60a5fa;
                  border-bottom-color: #374151;
                }
                .analysis-content h2:first-child {
                  margin-top: 0;
                }
                .analysis-content h3 {
                  font-size: 1rem;
                  font-weight: 600;
                  color: #1f2937;
                  margin-top: 1rem;
                  margin-bottom: 0.5rem;
                }
                .dark .analysis-content h3 {
                  color: #e5e7eb;
                }
                .analysis-content p {
                  color: #374151;
                  margin: 0.5rem 0;
                  line-height: 1.6;
                }
                .dark .analysis-content p {
                  color: #d1d5db;
                }
                .analysis-content ul, .analysis-content ol {
                  margin: 0.5rem 0;
                  padding-left: 1.5rem;
                }
                .analysis-content li {
                  color: #374151;
                  margin: 0.25rem 0;
                  line-height: 1.5;
                }
                .dark .analysis-content li {
                  color: #d1d5db;
                }
                .analysis-content strong {
                  color: #111827;
                  font-weight: 600;
                }
                .dark .analysis-content strong {
                  color: #f3f4f6;
                }
              `}</style>
              <ReactMarkdown>{documentAnalysis}</ReactMarkdown>
            </div>
          </div>

          <div className="flex gap-4">
            <button
              onClick={() => {
                setCurrentStep(1);
                setDocumentAnalysis(null);
                setAnalyzeFile(null);
                setAnalyzeDocumentText('');
                setExtractedFields(null);
              }}
              className="px-4 py-2 bg-blue-600 dark:bg-blue-700 hover:bg-blue-700 dark:hover:bg-blue-600 text-white rounded-lg transition-colors"
            >
              {t('employment:analyze.analyzeAnother', 'Analyze Another Document')}
            </button>
          </div>
        </>
      ) : (
        <div className="text-center py-12 text-gray-500 dark:text-gray-400">
          {t('employment:analyze.noResults', 'No analysis results yet. Go back and upload a document.')}
        </div>
      )}
    </div>
  );

  // Render combined issue and details step (Generate workflow)
  const renderIssueAndDetails = () => {
    const selectedType = situationTypes.find(s => s.id === selectedSituationType);

    return (
      <div className="space-y-6">
        {/* Issue Type Dropdown */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            {t('employment:generate.issueType', 'Issue Type')} *
          </label>
          <select
            value={selectedSituationType}
            onChange={(e) => setSelectedSituationType(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
          >
            <option value="">{t('employment:generate.selectIssueType', 'Select an issue type...')}</option>
            {situationTypes.map((type) => (
              <option key={type.id} value={type.id}>
                {type.name}
              </option>
            ))}
          </select>
          {selectedType && (
            <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
              {selectedType.description}
            </p>
          )}
        </div>

        {/* Show details form only when issue type is selected */}
        {selectedSituationType && (
          <>
            {/* Your Information Section */}
            <div className="space-y-4">
              <h5 className="font-medium text-gray-900 dark:text-white border-b border-gray-200 dark:border-gray-700 pb-2">
                {t('employment:generate.yourInfo', 'Your Information')}
              </h5>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('employment:generate.yourName', 'Your Name')}
                  </label>
                  <input
                    type="text"
                    value={letterEmployeeName}
                    onChange={(e) => setLetterEmployeeName(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    placeholder={t('employment:generate.yourNamePlaceholder', 'John Smith')}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('employment:generate.yourAddress', 'Your Street Address')}
                  </label>
                  <input
                    type="text"
                    value={letterEmployeeAddress}
                    onChange={(e) => setLetterEmployeeAddress(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    placeholder={t('employment:generate.yourAddressPlaceholder', '456 Main Street')}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('employment:generate.yourCity', 'City, State')}
                  </label>
                  <input
                    type="text"
                    value={letterEmployeeCity}
                    onChange={(e) => setLetterEmployeeCity(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    placeholder={t('employment:generate.yourCityPlaceholder', 'Los Angeles, CA')}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('employment:generate.yourZip', 'Zip Code')}
                  </label>
                  <input
                    type="text"
                    value={letterEmployeeZip}
                    onChange={(e) => setLetterEmployeeZip(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    placeholder={t('employment:generate.yourZipPlaceholder', '90001')}
                  />
                </div>
              </div>
            </div>

            {/* Employer Information Section */}
            <div className="space-y-4">
              <h5 className="font-medium text-gray-900 dark:text-white border-b border-gray-200 dark:border-gray-700 pb-2">
                {t('employment:generate.employerInfo', 'Employer Information')}
              </h5>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('employment:generate.employerName', 'Employer/Company Name')}
                  </label>
                  <input
                    type="text"
                    value={letterEmployerName}
                    onChange={(e) => setLetterEmployerName(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    placeholder={t('employment:generate.employerNamePlaceholder', 'ABC Corporation')}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('employment:generate.employerAddress', 'Employer Address')}
                  </label>
                  <input
                    type="text"
                    value={letterEmployerAddress}
                    onChange={(e) => setLetterEmployerAddress(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    placeholder={t('employment:generate.employerAddressPlaceholder', '123 Business Ave, City, State 12345')}
                  />
                </div>
              </div>
            </div>

            {/* Employment Details Section */}
            <div className="space-y-4">
              <h5 className="font-medium text-gray-900 dark:text-white border-b border-gray-200 dark:border-gray-700 pb-2">
                {t('employment:generate.employmentDetails', 'Employment Details')}
              </h5>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('employment:generate.position', 'Your Position/Title')}
                  </label>
                  <input
                    type="text"
                    value={letterPosition}
                    onChange={(e) => setLetterPosition(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    placeholder={t('employment:generate.positionPlaceholder', 'Software Engineer')}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('employment:generate.employmentDates', 'Employment Dates')}
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="date"
                      value={letterStartDate}
                      onChange={(e) => setLetterStartDate(e.target.value)}
                      className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    />
                    <span className="py-2 text-gray-500">-</span>
                    <input
                      type="date"
                      value={letterEndDate}
                      onChange={(e) => setLetterEndDate(e.target.value)}
                      className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Situation Description Section */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('employment:generate.situationDetails', 'Describe Your Situation')} *
              </label>
              <textarea
                value={letterDetails}
                onChange={(e) => setLetterDetails(e.target.value)}
                rows={6}
                placeholder={t('employment:generate.situationPlaceholder', 'Describe what happened, when it occurred, who was involved, and any relevant details...')}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                required
              />
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                {t('employment:generate.detailsHint', 'The more detail you provide, the more specific and effective your letter will be.')}
              </p>
            </div>
          </>
        )}
      </div>
    );
  };

  // Render generated letter step
  const renderGeneratedLetter = () => (
    <div className="space-y-6">
      {isLoading ? (
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-green-500 border-t-transparent"></div>
          <p className="mt-4 text-gray-600 dark:text-gray-400">
            {t('employment:generate.generating', 'Generating your letter...')}
          </p>
        </div>
      ) : generatedLetter ? (
        <>
          <div className="p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
            <div className="flex justify-between items-start">
              <div>
                <h4 className="font-medium text-green-900 dark:text-green-100 mb-2 flex items-center">
                  <CheckCircleIcon className="h-5 w-5 mr-2" />
                  {t('employment:generate.letterReady', 'Your Letter is Ready')}
                </h4>
                <p className="text-sm text-green-700 dark:text-green-300">
                  {t('employment:generate.letterReadyHint', 'Review the letter below and export it when you\'re satisfied.')}
                </p>
              </div>
              <ExportDropdownMenu
                onExport={handleExportLetter}
                onPrint={printLetter}
                showPrint={true}
              />
            </div>
          </div>

          <div className="p-6 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm">
            <pre className="whitespace-pre-wrap text-sm text-gray-800 dark:text-gray-200 font-sans leading-relaxed">
              {generatedLetter}
            </pre>
          </div>

          {letterTips.length > 0 && (
            <div className="p-4 bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800 rounded-lg">
              <h4 className="font-medium text-blue-900 dark:text-blue-100 mb-2">
                {t('employment:generate.tips', 'Tips')}
              </h4>
              <ul className="list-disc list-inside text-sm text-blue-800 dark:text-blue-200 space-y-1">
                {letterTips.map((tip, idx) => (
                  <li key={idx}>{tip}</li>
                ))}
              </ul>
            </div>
          )}

          <div className="flex flex-wrap gap-4">
            <button
              onClick={() => {
                setCurrentStep(1); // Go back to issueAndDetails step
                setGeneratedLetter(null);
              }}
              className="px-4 py-2 bg-blue-600 dark:bg-blue-700 hover:bg-blue-700 dark:hover:bg-blue-600 text-white rounded-lg font-medium transition-colors"
            >
              {t('employment:generate.editDetails', 'Edit Details')}
            </button>
            <button
              onClick={() => {
                resetState();
                setSelectedWorkflow('generate');
              }}
              className="px-4 py-2 bg-blue-600 dark:bg-blue-700 hover:bg-blue-700 dark:hover:bg-blue-600 text-white rounded-lg font-medium transition-colors"
            >
              {t('employment:generate.createAnother', 'Create Another Letter')}
            </button>
          </div>
        </>
      ) : (
        <div className="text-center py-12 text-gray-500 dark:text-gray-400">
          {t('employment:generate.noLetter', 'No letter generated yet. Go back and provide your details.')}
        </div>
      )}
    </div>
  );

  // Render step content based on workflow and step
  const renderStepContent = () => {
    const steps = getCurrentSteps();
    const currentStepName = steps[currentStep];

    switch (currentStepName) {
      case 'jurisdiction':
        return renderJurisdiction();
      case 'uploadDocument':
        return renderUploadDocument();
      case 'analysis':
        return renderAnalysisResults();
      case 'issueAndDetails':
        return renderIssueAndDetails();
      case 'generatedLetter':
        return renderGeneratedLetter();
      default:
        return null;
    }
  };

  const getStepTitle = () => {
    const steps = getCurrentSteps();
    const currentStepName = steps[currentStep];

    const titles: Record<string, string> = {
      jurisdiction: t('employment:stepTitles.jurisdiction', 'Select Your Location'),
      uploadDocument: t('employment:stepTitles.uploadDocument', 'Upload Document'),
      analysis: t('employment:stepTitles.analysis', 'Analysis Results'),
      issueAndDetails: t('employment:stepTitles.issueAndDetails', 'Issue & Details'),
      generatedLetter: t('employment:stepTitles.generatedLetter', 'Your Letter'),
    };

    return titles[currentStepName] || currentStepName;
  };

  const getStepIcon = (stepName: string) => {
    const icons: Record<string, React.ElementType> = {
      jurisdiction: MapPinIcon,
      uploadDocument: ArrowUpTrayIcon,
      analysis: DocumentMagnifyingGlassIcon,
      issueAndDetails: PencilSquareIcon,
      generatedLetter: DocumentTextIcon,
    };
    return icons[stepName] || DocumentTextIcon;
  };

  // Main render
  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-8">
      {/* Loading Overlay for document processing */}
      <LoadingOverlay
        isVisible={isLoading}
        message={t('employment:loading.processing', 'Processing...')}
        subMessage={t('employment:loading.pleaseWait', 'Analyzing your document')}
      />

      <div className="max-w-4xl mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center mb-4">
            <BriefcaseIcon className="h-12 w-12 text-blue-600 dark:text-blue-400" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
            {t('employment:title', 'Employment Advisor')}
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            {t('employment:subtitle', 'Analyze employment documents and generate professional workplace letters')}
          </p>
        </div>

        {/* Workflow Selection or Step Progress */}
        {selectedWorkflow && (
          <div className="mb-8">
            <div className="flex items-center justify-between">
              {getCurrentSteps().map((step, index) => {
                const Icon = getStepIcon(step);
                const isActive = index === currentStep;
                const isCompleted = index < currentStep;

                return (
                  <React.Fragment key={step}>
                    <div className="flex flex-col items-center">
                      <button
                        onClick={() => index < currentStep && setCurrentStep(index)}
                        disabled={index > currentStep}
                        className={`w-10 h-10 rounded-full flex items-center justify-center transition-colors ${
                          isActive
                            ? selectedWorkflow === 'analyze'
                              ? 'bg-blue-600 dark:bg-blue-500 text-white'
                              : 'bg-green-600 dark:bg-green-500 text-white'
                            : isCompleted
                            ? selectedWorkflow === 'analyze'
                              ? 'bg-blue-500 text-white cursor-pointer'
                              : 'bg-green-500 text-white cursor-pointer'
                            : 'bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-400'
                        } ${index < currentStep ? 'cursor-pointer' : index > currentStep ? 'cursor-not-allowed' : ''}`}
                      >
                        {isCompleted ? (
                          <CheckCircleIcon className="h-6 w-6" />
                        ) : (
                          <Icon className="h-5 w-5" />
                        )}
                      </button>
                      <span
                        className={`text-xs mt-2 hidden sm:block text-center max-w-[80px] ${
                          isActive
                            ? selectedWorkflow === 'analyze'
                              ? 'text-blue-600 dark:text-blue-400 font-medium'
                              : 'text-green-600 dark:text-green-400 font-medium'
                            : 'text-gray-500 dark:text-gray-400'
                        }`}
                      >
                        {t(`employment:steps.${step}`, step.replace(/([A-Z])/g, ' $1').trim())}
                      </span>
                    </div>
                    {index < getCurrentSteps().length - 1 && (
                      <div
                        className={`flex-1 h-1 mx-2 rounded ${
                          index < currentStep
                            ? selectedWorkflow === 'analyze'
                              ? 'bg-blue-500'
                              : 'bg-green-500'
                            : 'bg-gray-200 dark:bg-gray-700'
                        }`}
                      />
                    )}
                  </React.Fragment>
                );
              })}
            </div>
          </div>
        )}

        {/* Main Content Card */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6 mb-6">
          {selectedWorkflow ? (
            <>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-6">
                {getStepTitle()}
              </h2>

              {error && (
                <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg">
                  <p className="text-red-700 dark:text-red-300">{error}</p>
                </div>
              )}

              {renderStepContent()}
            </>
          ) : (
            renderWorkflowSelection()
          )}
        </div>

        {/* Navigation Buttons */}
        {selectedWorkflow && (
          <div className="flex justify-between">
            <button
              onClick={handleBack}
              className="flex items-center px-6 py-3 bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-800 dark:text-white rounded-lg font-medium transition-colors"
            >
              <ArrowLeftIcon className="h-5 w-5 mr-2" />
              {currentStep === 0
                ? t('employment:navigation.changeWorkflow', 'Change Workflow')
                : t('employment:navigation.back', 'Back')}
            </button>

            {currentStep < getCurrentSteps().length - 1 && (
              <button
                onClick={handleNext}
                disabled={!canProceed() || isLoading}
                className={`flex items-center px-6 py-3 ${
                  selectedWorkflow === 'analyze'
                    ? 'bg-blue-600 dark:bg-blue-700 hover:bg-blue-700 dark:hover:bg-blue-600'
                    : 'bg-green-600 dark:bg-green-700 hover:bg-green-700 dark:hover:bg-green-600'
                } text-white rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed`}
              >
                {isLoading ? (
                  <>
                    <svg className="animate-spin h-5 w-5 mr-2" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    {t('employment:navigation.processing', 'Processing...')}
                  </>
                ) : (
                  <>
                    {t('employment:navigation.next', 'Next')}
                    <ArrowRightIcon className="h-5 w-5 ml-2" />
                  </>
                )}
              </button>
            )}
          </div>
        )}

        {/* Disclaimer */}
        <div className="mt-8 p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
          <div className="flex items-start">
            <ExclamationTriangleIcon className="h-5 w-5 text-yellow-600 dark:text-yellow-400 mt-0.5 mr-3 flex-shrink-0" />
            <div>
              <h4 className="font-medium text-yellow-800 dark:text-yellow-200">
                {t('employment:disclaimer.title', 'Legal Disclaimer')}
              </h4>
              <p className="text-sm text-yellow-700 dark:text-yellow-300 mt-1">
                {t('employment:disclaimer.text', 'This tool provides general information about employment rights and generates documents for educational purposes only. It does not constitute legal advice. Employment laws vary by jurisdiction and change over time. Always verify information with local authorities and consider consulting with a licensed employment attorney for advice specific to your situation.')}
              </p>
            </div>
          </div>
        </div>
      </div>

    </div>
  );
};
